import { z } from 'zod';
import { articleUnitSchema } from '../inventory/article.schema';
import { engineeringBomItemTypeSchema, engineeringBomKindSchema } from './bom.schema';
import {
  ASSEMBLY_KEYS,
  BOM_DERIVED_FIELDS,
  BOM_QUANTITY_SOURCES,
  CHAIN_CALC_FIELDS,
  LAYOUT_FIELDS,
} from './bom-template';

/**
 * Stücklisten-Vorlage (VB-26025 Stufe B) — Schemas für Pflege und Abruf.
 *
 * Die Vorlage ersetzt das Excel-Blatt „Haupteingabe". Sie ist **im ERP pflegbar**
 * (Entscheidung Tech-Lead 17.08.): einmalig aus der Excel-Vorlage geladen, danach
 * ohne Excel änderbar. Der Revisionsstand („Rev16") bleibt sichtbar, damit
 * nachvollziehbar ist, welcher Stand eine Stückliste erzeugt hat.
 */

export const bomQuantitySourceSchema = z.enum(BOM_QUANTITY_SOURCES);
export const chainCalcFieldSchema = z.enum(CHAIN_CALC_FIELDS);
export const bomDerivedFieldSchema = z.enum(BOM_DERIVED_FIELDS);
export const layoutFieldSchema = z.enum(LAYOUT_FIELDS);
export const assemblyKeySchema = z.enum(ASSEMBLY_KEYS);

/** Obergrenze je Vorlage. Die Rev16 hat 250 Kandidatenzeilen. */
export const MAX_BOM_TEMPLATE_ROWS = 1000;

export const bomTemplateResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Revisionsstand der Quelle („Rev16") bzw. der Pflege im ERP. */
  revision: z.string(),
  kind: engineeringBomKindSchema,
  isActive: z.boolean(),
  note: z.string().nullable(),
  /** Anzahl Kandidatenzeilen — für die Übersicht. */
  rowCount: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type BomTemplateResponse = z.infer<typeof bomTemplateResponseSchema>;

/**
 * Eine Kandidatenzeile. Alles außer der Menge ist hier vorbelegt — genau das
 * macht die Stückliste „zu 90 % fertig".
 */
export const bomTemplateRowResponseSchema = z.object({
  id: z.string().uuid(),
  templateId: z.string().uuid(),
  /** Reihenfolge; in der Excel-Vorlage die Zeilennummer des Blattes. */
  position: z.number().int(),
  /** Abschnitt, zu dem die Zeile gehört (auch die Überschrift selbst trägt ihn). */
  section: z.string(),
  type: engineeringBomItemTypeSchema,
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string().nullable(),
  description: z.string(),
  descriptionEn: z.string().nullable(),
  unit: articleUnitSchema.nullable(),
  groupCode: z.string().nullable(),
  materialText: z.string().nullable(),
  supplierName: z.string().nullable(),
  defaultLengthMm: z.number().nullable(),
  quantitySource: bomQuantitySourceSchema,
  chainField: chainCalcFieldSchema.nullable(),
  /** Bei `LAYOUT`: welcher Wert der Projektauslegung (mal `derivedFactor`). */
  layoutField: layoutFieldSchema.nullable(),
  /** Baugruppe der Auslegung, zu der diese Zeile gehört — bildet den Block (ADR 0097). */
  assemblyKey: assemblyKeySchema.nullable(),
  /** Auslegungszeile, deren Artikel (und ggf. Menge) diese Zeile übernimmt (ADR 0097). */
  layoutTemplateKey: z.string().nullable(),
  /** Position der übergeordneten Zeile — Artikel eingerückt unter Artikel (ADR 0099). */
  parentPosition: z.number().int().nullable(),
  derivedFromPositions: z.array(z.number().int()),
  derivedField: bomDerivedFieldSchema.nullable(),
  derivedFactor: z.number(),
  note: z.string().nullable(),
});
export type BomTemplateRowResponse = z.infer<typeof bomTemplateRowResponseSchema>;

/** Vorlage mit ihren Zeilen — ein Abruf, weil die Maske beides braucht. */
export const bomTemplateDetailResponseSchema = bomTemplateResponseSchema.extend({
  rows: z.array(bomTemplateRowResponseSchema),
});
export type BomTemplateDetailResponse = z.infer<typeof bomTemplateDetailResponseSchema>;

export const createBomTemplateSchema = z.object({
  name: z.string().trim().min(1, 'Bezeichnung ist erforderlich.').max(200),
  revision: z.string().trim().min(1, 'Revisionsstand ist erforderlich.').max(40),
  kind: engineeringBomKindSchema.default('TWO_D'),
  isActive: z.boolean().default(true),
  note: z.string().trim().max(2000).nullish(),
});
export type CreateBomTemplateInput = z.infer<typeof createBomTemplateSchema>;

export const updateBomTemplateSchema = z.object({
  name: z.string().trim().min(1, 'Bezeichnung ist erforderlich.').max(200).optional(),
  revision: z.string().trim().min(1, 'Revisionsstand ist erforderlich.').max(40).optional(),
  kind: engineeringBomKindSchema.optional(),
  isActive: z.boolean().optional(),
  note: z.string().trim().max(2000).nullish(),
});
export type UpdateBomTemplateInput = z.infer<typeof updateBomTemplateSchema>;

/** Eine Zeile beim Speichern. Ohne `id` = neu. */
export const bomTemplateRowDraftSchema = z.object({
  id: z.string().uuid().nullish(),
  position: z.number().int().min(1).max(100_000),
  section: z.string().trim().min(1, 'Abschnitt ist erforderlich.').max(120),
  type: engineeringBomItemTypeSchema,
  articleId: z.string().uuid().nullish(),
  articleNumber: z.string().trim().max(60).nullish(),
  description: z.string().trim().min(1, 'Bezeichnung ist erforderlich.').max(300),
  descriptionEn: z.string().trim().max(300).nullish(),
  unit: articleUnitSchema.nullish(),
  groupCode: z.string().trim().max(20).nullish(),
  materialText: z.string().trim().max(200).nullish(),
  supplierName: z.string().trim().max(200).nullish(),
  defaultLengthMm: z.number().positive('Die Länge muss größer als 0 sein.').max(1_000_000).nullish(),
  quantitySource: bomQuantitySourceSchema.default('MANUAL'),
  chainField: chainCalcFieldSchema.nullish(),
  layoutField: layoutFieldSchema.nullish(),
  assemblyKey: assemblyKeySchema.nullish(),
  layoutTemplateKey: z.string().trim().min(1).max(60).nullish(),
  parentPosition: z.number().int().min(1).max(100_000).nullish(),
  derivedFromPositions: z.array(z.number().int().min(1).max(100_000)).max(200).default([]),
  derivedField: bomDerivedFieldSchema.nullish(),
  derivedFactor: z.number().positive('Der Faktor muss größer als 0 sein.').max(10_000).default(1),
  note: z.string().trim().max(2000).nullish(),
});
export type BomTemplateRowDraft = z.infer<typeof bomTemplateRowDraftSchema>;

/**
 * Alle Zeilen einer Vorlage in einem Vorgang. Wie bei der Stückliste selbst:
 * eine Transaktion, ein Audit-Eintrag, und Zeilen, die nicht mehr dabei sind,
 * verschwinden.
 *
 * Geprüft wird hier, was die reine Logik später **voraussetzt** — sie kann eine
 * kaputte Vorlage nur melden, nicht heilen. Also: Positionen eindeutig, jede
 * Quelle mit ihren Pflichtangaben.
 */
export const saveBomTemplateRowsSchema = z
  .object({
    rows: z
      .array(bomTemplateRowDraftSchema)
      .max(MAX_BOM_TEMPLATE_ROWS, `Höchstens ${MAX_BOM_TEMPLATE_ROWS} Zeilen je Vorlage.`),
  })
  .superRefine((value, ctx) => {
    const positions = new Set<number>();
    value.rows.forEach((row, index) => {
      const at = (field: string) => ({ path: ['rows', index, field] as (string | number)[] });

      if (positions.has(row.position)) {
        ctx.addIssue({ code: 'custom', message: `Position ${row.position} kommt doppelt vor.`, ...at('position') });
      }
      positions.add(row.position);

      // Abschnitt und Zusatztext sind Text — sie tragen keine Menge, also auch
      // keine Mengenquelle außer „von Hand" (die dort nie greift).
      if (row.type !== 'POSITION' && row.quantitySource !== 'MANUAL') {
        ctx.addIssue({
          code: 'custom',
          message: 'Abschnitt und Zusatztext haben keine Mengenquelle.',
          ...at('quantitySource'),
        });
      }
      if (row.quantitySource === 'CHAIN_CALC' && !row.chainField) {
        ctx.addIssue({
          code: 'custom',
          message: 'Zur Kettenberechnung gehört das Feld, aus dem die Menge kommt.',
          ...at('chainField'),
        });
      }
      if (row.quantitySource === 'LAYOUT' && !row.layoutField) {
        ctx.addIssue({
          code: 'custom',
          message: 'Zur Projektauslegung gehört der Wert, aus dem die Menge kommt.',
          ...at('layoutField'),
        });
      }
      if (row.quantitySource === 'LENGTH_TIMES_COUNT' && !row.defaultLengthMm) {
        ctx.addIssue({
          code: 'custom',
          message: 'Meterware braucht eine Länge je Stück.',
          ...at('defaultLengthMm'),
        });
      }
      if (row.quantitySource === 'DERIVED') {
        if (row.derivedFromPositions.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: 'Eine abgeleitete Menge braucht mindestens eine Quellzeile.',
            ...at('derivedFromPositions'),
          });
        }
        if (!row.derivedField) {
          ctx.addIssue({
            code: 'custom',
            message: 'Es fehlt, ob Menge oder Stückzahl der Quellzeile eingeht.',
            ...at('derivedField'),
          });
        }
        if (row.derivedFromPositions.includes(row.position)) {
          ctx.addIssue({
            code: 'custom',
            message: 'Eine Zeile kann sich nicht aus sich selbst ableiten.',
            ...at('derivedFromPositions'),
          });
        }
      }
    });

    // Verweise auf Zeilen, die es in dieser Vorlage nicht gibt: die reine Logik
    // meldet sie beim Füllen — hier lassen sie sich gar nicht erst speichern.
    value.rows.forEach((row, index) => {
      const missing = row.derivedFromPositions.filter((position) => !positions.has(position));
      if (missing.length > 0) {
        ctx.addIssue({
          code: 'custom',
          message: `Es gibt keine Zeile mit Position ${missing.join(', ')}.`,
          path: ['rows', index, 'derivedFromPositions'],
        });
      }

      // Hierarchie (ADR 0099): Die Elternzeile muss existieren und VOR ihrem
      // Kind stehen — sonst wäre „Elternteil vor Kind" beim Füllen verletzt,
      // was das Speichern der Stückliste selbst schon verlangt (`refineBomItems`
      // in `bom.schema.ts`). Ein Zirkel entsteht so gar nicht erst.
      if (row.parentPosition != null) {
        if (!positions.has(row.parentPosition)) {
          ctx.addIssue({
            code: 'custom',
            message: `Es gibt keine Zeile mit Position ${row.parentPosition}.`,
            path: ['rows', index, 'parentPosition'],
          });
        } else if (row.parentPosition >= row.position) {
          ctx.addIssue({
            code: 'custom',
            message: 'Die übergeordnete Zeile muss vor dieser Zeile stehen.',
            path: ['rows', index, 'parentPosition'],
          });
        }
      }
    });
  });
export type SaveBomTemplateRowsInput = z.infer<typeof saveBomTemplateRowsSchema>;

/** Ergebnis des Speicherns — was tatsächlich passiert ist. */
export const saveBomTemplateRowsResultSchema = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  removed: z.number().int(),
});
export type SaveBomTemplateRowsResult = z.infer<typeof saveBomTemplateRowsResultSchema>;

/**
 * Abgeleitetes Befestigungsmaterial (VB-26025 Stufe C) — Antwort der Vorschau.
 * Gerechnet wird mit `deriveFasteners`; hier steht nur, wie das Ergebnis über die
 * Leitung geht.
 */
export const fastenerLineResponseSchema = z.object({
  articleId: z.string().uuid(),
  articleNumber: z.string(),
  articleName: z.string(),
  /**
   * Einheit als Freitext, nicht als Enum: Die Kit-Kante speichert einen Snapshot
   * (`bom_items.unit` ist `String?`), und der kann aus Altdaten alles enthalten.
   * Die Anzeige geht über `formatUnit`, das tolerant ist.
   */
  unit: z.string().nullable(),
  quantity: z.number(),
  /** Woher die Menge kommt — je Bauteil, das dieses Teil braucht. */
  sources: z.array(
    z.object({
      articleNumber: z.string(),
      description: z.string(),
      quantity: z.number(),
      perUnit: z.number(),
    }),
  ),
});
export type FastenerLineResponse = z.infer<typeof fastenerLineResponseSchema>;

/**
 * Der Satz EINES Bauteils — so wird übernommen (ADR 0095): Die Teile hängen
 * eingerückt unter `itemId`, ihre Menge ist die Menge je Einheit des Bauteils.
 */
export const fastenerGroupResponseSchema = z.object({
  /** Zeile der Stückliste, unter der die Teile landen. */
  itemId: z.string().uuid(),
  posNumber: z.string(),
  articleNumber: z.string(),
  description: z.string(),
  /** Stückzahl bei Meterware, sonst Gesamtmenge des Bauteils im Baum. */
  driver: z.number(),
  lines: z.array(
    z.object({
      articleId: z.string().uuid(),
      articleNumber: z.string(),
      articleName: z.string(),
      /** Freitext-Snapshot der Kit-Kante — siehe `fastenerLineResponseSchema`. */
      unit: z.string().nullable(),
      /** Menge je EINER Einheit des Bauteils. */
      perUnit: z.number(),
      /** `perUnit × driver` — nur für die Vorschau; gerechnet wird im Baum. */
      total: z.number(),
    }),
  ),
});
export type FastenerGroupResponse = z.infer<typeof fastenerGroupResponseSchema>;

export const fastenerDeriveResponseSchema = z.object({
  /** Je Bauteil ein Satz, in Baum-Reihenfolge. */
  groups: z.array(fastenerGroupResponseSchema),
  /** Dieselben Teile kumuliert — die Kopfzahlen des Dialogs. */
  lines: z.array(fastenerLineResponseSchema),
  /** Was nicht abgeleitet werden konnte — benannt, nicht verschluckt. */
  issues: z.array(z.string()),
  summary: z.object({
    components: z.number().int(),
    withoutArticle: z.number().int(),
    totalQuantity: z.number(),
  }),
});
export type FastenerDeriveResponse = z.infer<typeof fastenerDeriveResponseSchema>;

/**
 * Werte der Projektauslegung, aus denen eine Stücklistenmenge folgen kann
 * (ADR 0094). Zwei Wege in einer Antwort:
 * - `values`: die benannten Zahlen (Weißliste `LAYOUT_FIELDS`).
 * - `articles`: was die Auslegung an Artikeln gewählt hat, mit Menge und
 *   Ablängung — damit ist eine Vorlagenzeile schon gefüllt, sobald ihr Artikel
 *   in der Auslegung steht, ohne dass die Vorlage etwas dafür konfiguriert.
 */
export const bomLayoutValuesResponseSchema = z.object({
  projectId: z.string().uuid(),
  values: z.record(layoutFieldSchema, z.number().nullable()),
  articles: z.array(
    z.object({
      articleId: z.string().uuid(),
      quantity: z.number().nullable(),
      /** Länge je Stück in mm (ADR 0040); gesetzt ⇒ `quantity` ist die Stückzahl. */
      cutLengthMm: z.number().int().nullable(),
    }),
  ),
  /**
   * Dieselben Zeilen, je Zeilenschlüssel — die Grundlage der Stücklisten-Blöcke
   * (ADR 0097). Trägt den Artikel, nicht nur die Menge: Vorlagenzeilen mit
   * `layoutTemplateKey` übernehmen ihn von hier.
   */
  byTemplateKey: z.array(
    z.object({
      templateKey: z.string(),
      articleId: z.string().uuid().nullable(),
      articleNumber: z.string().nullable(),
      articleName: z.string().nullable(),
      unit: z.string().nullable(),
      quantity: z.number().nullable(),
      cutLengthMm: z.number().int().nullable(),
    }),
  ),
  /** Was in der Auslegung fehlt — in der Sprache des Datenblatts. */
  missing: z.array(z.string()),
});
export type BomLayoutValuesResponse = z.infer<typeof bomLayoutValuesResponseSchema>;
